import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  Button,
  Drawer,
  ErrorState,
  Inline,
  Stack,
  TextInput,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiSend, UnauthenticatedError } from "../lib/api.js";
import {
  buildBody,
  emptyDraft,
  isoToLocalInputValue,
  isValid,
  validateDraft,
  type LifecycleDateAction,
  type LifecycleDatesDraft,
} from "./lifecycleDatesValidation.js";

/**
 * Sprint 33 (GT-TENANT-ADMIN-COMPLETE-02) — Tenant 360° lifecycle
 * date drawer.
 *
 * Wraps the existing lifecycle endpoints (Sprint 23 Wave B4):
 *
 *   POST /v1/admin/tenants/:id/lifecycle/mark-trial
 *     body: { trialUntil }
 *   POST /v1/admin/tenants/:id/lifecycle/set-valid-until
 *     body: { validUntil, setActive? }
 *
 * **Lifecycle, not metadata.** The Sprint 32 `<TenantEditDialog>`
 * deliberately does NOT edit `trialUntil` / `validUntil` because
 * those are owned by lifecycle actions, not the metadata patch route.
 * This dialog is the operator-facing wrapper for those lifecycle
 * actions; it never sends a metadata PATCH.
 *
 * Behaviour:
 *   - Two modes selected via `action`:
 *       - `mark-trial`        → flips status to `trial`, sets trialUntil
 *       - `set-valid-until`   → sets validUntil; optional setActive
 *                               flips the status to `active`
 *   - For `set-valid-until` the operator can choose "Clear valid until"
 *     which sends `{ validUntil: null }`. (The backend supports null.)
 *   - Save is disabled when the draft equals the current value
 *     (prevents redundant audit rows).
 *   - Errors render via the shared <ErrorState> with requestId.
 */

export interface TenantLifecycleDatesDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly tenantId: string;
  readonly action: LifecycleDateAction;
  /** Current ISO value on the tenant — drives the change-only guard. */
  readonly currentTrialUntil: string | null;
  readonly currentValidUntil: string | null;
}

export function TenantLifecycleDatesDialog(props: TenantLifecycleDatesDialogProps) {
  const { open, onClose, tenantId, action, currentTrialUntil, currentValidUntil } = props;
  const { t } = useLocale();
  const qc = useQueryClient();

  const initialIso = action === "mark-trial" ? currentTrialUntil : currentValidUntil;
  const initialDraft: LifecycleDatesDraft = useMemo(
    () => ({
      ...emptyDraft(),
      localValue: isoToLocalInputValue(initialIso),
    }),
    [initialIso],
  );

  const [draft, setDraft] = useState<LifecycleDatesDraft>(initialDraft);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDraft(initialDraft);
      setSubmitError(null);
    }
  }, [open, initialDraft]);

  const validation = useMemo(() => validateDraft(draft, action), [draft, action]);
  const body = useMemo(() => buildBody(draft, action, initialIso), [draft, action, initialIso]);
  const canSave = body !== null && isValid(validation);

  const path = action === "mark-trial" ? "mark-trial" : "set-valid-until";

  const mutation = useMutation<unknown, Error, ReturnType<typeof buildBody>>({
    mutationFn: (payload) =>
      apiSend("POST", `/v1/admin/tenants/${tenantId}/lifecycle/${path}`, payload),
    onMutate: () => setSubmitError(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "tenants", tenantId, "detail"] });
      qc.invalidateQueries({ queryKey: ["admin", "tenants"] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof UnauthenticatedError) {
        setSubmitError(t("admin.tenant_360.error.session_expired_body"));
        return;
      }
      if (err instanceof ApiError) {
        const requestId = err.requestId;
        setSubmitError(
          requestId
            ? t("admin.tenant_360.lifecycle_dates.error.body_with_request", {
                status: err.status,
                requestId,
              })
            : t("admin.tenant_360.lifecycle_dates.error.body", { status: err.status }),
        );
        return;
      }
      setSubmitError(err.message);
    },
  });

  if (!open) return null;

  const titleKey =
    action === "mark-trial"
      ? "admin.tenant_360.lifecycle_dates.mark_trial.title"
      : "admin.tenant_360.lifecycle_dates.set_valid_until.title";
  const subtitleKey =
    action === "mark-trial"
      ? "admin.tenant_360.lifecycle_dates.mark_trial.subtitle"
      : "admin.tenant_360.lifecycle_dates.set_valid_until.subtitle";
  const fieldLabelKey =
    action === "mark-trial"
      ? "admin.tenant_360.lifecycle_dates.field.trial_until"
      : "admin.tenant_360.lifecycle_dates.field.valid_until";
  const submitKey =
    action === "mark-trial"
      ? "admin.tenant_360.lifecycle_dates.mark_trial.submit"
      : "admin.tenant_360.lifecycle_dates.set_valid_until.submit";

  return (
    <Drawer
      open={open}
      onClose={() => {
        if (mutation.isPending) return;
        onClose();
      }}
      ariaLabel={t(titleKey)}
    >
      <form
        data-testid="admin-tenant-360-lifecycle-dates-form"
        data-lifecycle-action={action}
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSave || body === null) return;
          mutation.mutate(body);
        }}
        style={{ display: "flex", flexDirection: "column", gap: "var(--gt-sys-space-3)" }}
      >
        <Stack gap={1}>
          <strong style={{ fontSize: "var(--gt-sys-font-size-lg)" }}>{t(titleKey)}</strong>
          <span
            style={{
              color: "var(--gt-sys-text-muted)",
              fontSize: "var(--gt-sys-font-size-xs)",
            }}
          >
            {t(subtitleKey)}
          </span>
        </Stack>

        <TextInput
          label={t(fieldLabelKey)}
          type="datetime-local"
          value={draft.localValue}
          onChange={(e) => setDraft({ ...draft, localValue: e.currentTarget.value, clear: false })}
          aria-invalid={validation.localValue !== undefined}
          disabled={action === "set-valid-until" && draft.clear}
          data-testid="admin-tenant-360-lifecycle-dates-input"
        />
        {validation.localValue ? (
          <span
            role="alert"
            style={errorTextStyle}
            data-testid="admin-tenant-360-lifecycle-dates-input-error"
          >
            {t(`admin.tenant_360.lifecycle_dates.error.field.${validation.localValue}` as const)}
          </span>
        ) : null}

        {action === "set-valid-until" ? (
          <Stack gap={1}>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={draft.setActive}
                onChange={(e) => setDraft({ ...draft, setActive: e.currentTarget.checked })}
                data-testid="admin-tenant-360-lifecycle-dates-set-active"
                disabled={draft.clear}
              />
              <span style={{ fontSize: "var(--gt-sys-font-size-sm)" }}>
                {t("admin.tenant_360.lifecycle_dates.field.set_active")}
              </span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={draft.clear}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    clear: e.currentTarget.checked,
                    setActive: e.currentTarget.checked ? false : draft.setActive,
                  })
                }
                data-testid="admin-tenant-360-lifecycle-dates-clear"
              />
              <span style={{ fontSize: "var(--gt-sys-font-size-sm)" }}>
                {t("admin.tenant_360.lifecycle_dates.field.clear")}
              </span>
            </label>
          </Stack>
        ) : null}

        {submitError ? (
          <ErrorState
            title={t("admin.tenant_360.lifecycle_dates.error.title")}
            body={submitError}
            action={
              <Button
                variant="secondary"
                onClick={() => setSubmitError(null)}
                data-testid="admin-tenant-360-lifecycle-dates-error-dismiss"
              >
                {t("admin.tenants.error.dismiss")}
              </Button>
            }
          />
        ) : null}

        <Inline gap={2} justify="end" align="center" wrap>
          <Button
            type="button"
            variant="secondary"
            disabled={mutation.isPending}
            onClick={onClose}
            data-testid="admin-tenant-360-lifecycle-dates-cancel"
          >
            {t("admin.tenant_360.edit.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSave || mutation.isPending}
            data-testid="admin-tenant-360-lifecycle-dates-submit"
          >
            {mutation.isPending ? t("common.saving") : t(submitKey)}
          </Button>
        </Inline>
      </form>
    </Drawer>
  );
}

const errorTextStyle: CSSProperties = {
  color: "var(--gt-sys-danger)",
  fontSize: "var(--gt-sys-font-size-xs)",
};
